import { FC, useCallback, useEffect, useRef, useState } from 'react'
import {
  Link,
  Redirect,
  Route,
  RouteComponentProps,
  Switch,
  useHistory,
  useLocation,
} from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { OuterWrapper, TitleBox, TitleCountdownBox, TopWrapper } from './styles'
import { Box } from '../../shared_components/UI/Box'

// @ts-ignore
import { setIsDrops } from '../../modules/miscUi/actions'

import { selectCurrentProject } from '../../modules/missionControl/selectors'
import { setCurrentProject } from '../../modules/missionControl/slice'
import ChapterView from './ChapterView'
import AnimatedLoader from '../../shared_components/AnimatedLoader'

import { HelmetProvider } from '../../App'
import NotFound from './NotFound'
import { useLazyGetProjectByIdQuery } from '../../modules/missionControl/api'
import { setDrop } from '../../modules/drop/slice'
import { selectDrop } from '../../modules/drop/selectors'
import useMissionControlDropStateManagement from '../../hooks/useMissionControlDropStateManagement'
import ProjectLinks from '../../shared_components/ProjectLinks'
import routes from '../../router/routes'
import { isEmpty } from 'ramda'
import DropOverview from './DropOverview'
import useFetchDropsChaptersBySlug from '../../hooks/drops/useFetchDropsChaptersBySlug'
import { TokenLogo } from './parts'
import {
  DropStatus,
  getDropDateStatus,
  getIsDropChapterDisabled,
} from './helpers'
import { selectUser } from '../../modules/user/selectors'
import styled, { css } from 'styled-components'
import { H1, P, Strong } from '../../shared_components/Library/Typeography'
import { StyledThemedButton } from '../../shared_components/ThemedButton/styles'
import { IDrop } from '../../types/reducerTypes'
import { overflowingCarouselCssRules } from '../../shared_components/Library/OverflowingCarousel'
import { defaultTransition, useShouldShowForThreshold } from '../../theme'
import { useScrollPosition } from '../../hooks/useScrollPosition'
import { usePageMaxWidth } from '../../shared_components/Page'
import { pageWrapCssRules } from '../../shared_components/Header/styles'

export enum CardThemes {
  OPEN = 'OPEN',
  UPCOMING = 'UPCOMING',
  CLOSED = 'CLOSED',
}

function useDropsChapters(slug: string) {
  const dispatch = useDispatch()
  const user = useSelector(selectUser)

  const [currentDropChapterId, setCurrentDropChapterId] = useState<
    number | undefined
  >()

  const { drops, fetchDrops, isLoading } = useFetchDropsChaptersBySlug()

  useEffect(() => {
    fetchDrops(slug)
    dispatch(setIsDrops(true))
    return () => {
      dispatch(setIsDrops(false))
    }
  }, [dispatch, fetchDrops, slug])

  useEffect(() => {
    const liveDrop = drops.find(
      (drop) => getDropDateStatus(drop) === DropStatus.Live,
    )
    const closedDrop = drops
      .slice()
      .reverse()
      .find((drop) => getDropDateStatus(drop) === DropStatus.Closed)

    const drop = liveDrop ?? closedDrop ?? drops[0]

    if (!currentDropChapterId && !isEmpty(drops)) {
      setCurrentDropChapterId(drop.id)
    }

    const currentChapterDrop =
      drops.find((drop) => drop.id === currentDropChapterId) ?? drop

    dispatch(setCurrentProject(currentChapterDrop?.project))
    dispatch(setDrop(currentChapterDrop))
  }, [dispatch, drops, currentDropChapterId, user?.walletAddress])

  const onDropChapterChange = useCallback(
    (id: number) => {
      setCurrentDropChapterId(id)
    },
    [setCurrentDropChapterId],
  )

  return {
    drops,
    loading: isLoading,
    currentDropChapterId,
    onDropChapterChange,
    currentDropChapterIndex:
      drops.findIndex((drop) => drop.id === currentDropChapterId) ?? 0,
  }
}

export const numberWords = {
  1: 'One',
  2: 'Two',
  3: 'Three',
  4: 'Four',
}

interface MatchParams {
  slug: string
}

const DropDetails: FC<RouteComponentProps<MatchParams>> = ({
  match: {
    params: { slug },
  },
}) => {
  const dispatch = useDispatch()

  const { loading } = useDropsChapters(slug)

  const drop = useSelector(selectDrop)

  const [fetchProject] = useLazyGetProjectByIdQuery()

  const getAndSetCurrentProject = useCallback(
    async (projectId: number) => {
      try {
        const { project: fetchedProject } = await fetchProject({
          projectId,
        }).unwrap()
        if (!fetchedProject) throw new Error('Project not found')
        dispatch(setCurrentProject(fetchedProject))
      } catch (error) {
        console.error(error)
      }
    },
    [dispatch, fetchProject],
  )

  useEffect(() => {
    if (!drop) return
    getAndSetCurrentProject(drop?.projectId)
  }, [drop, getAndSetCurrentProject])

  if (loading) return <AnimatedLoader onlyWhite />
  if (!drop) return <NotFound />

  return (
    <>
      <HelmetProvider title={`ZAP | ${drop.name}`} />
      <InnerDropDetails slug={slug} />
    </>
  )
}

export default DropDetails

const InnerDropDetails = ({ slug }: { slug: string }) => {
  const { drops, currentDropChapterId, onDropChapterChange } =
    useDropsChapters(slug)

  useMissionControlDropStateManagement()

  const drop = useSelector(selectDrop)
  const currentProject = useSelector(selectCurrentProject)

  const topBlockRef = useRef<HTMLDivElement>(null)

  if (!drop) return <NotFound />

  const { id: dropId, about, media, name } = drop ?? {}
  const { logo } = about ?? {}
  const { topBanner } = media ?? {}

  if (!dropId) return <></>

  const dropsWithChapters = drops
    .sort((a, b) => a.id - b.id)
    .map((drop, i) => ({
      ...drop,
      chapterNumber: i + 1,
    }))

  return (
    <>
      <Box padV={20}>
        <OuterWrapper dc relative>
          <Box dc relative ref={topBlockRef} g={28}>
            <Banner src={topBanner?.url ?? '/hero-cover-drops.png'} />
            <TopWrapper dc jsb g={60}>
              <Box jsb g={32}>
                <TitleCountdownBox afs>
                  <TitleBox dc g={16} padT={12} padB={16}>
                    <Box ac jsb g={16}>
                      <Box ac g={8}>
                        {logo?.url && (
                          <TokenLogo src={logo?.url} alt={`${name} logo`} />
                        )}
                        <H1 s="2XL" fw="bolder" uc>
                          {name}
                        </H1>
                      </Box>
                      <Box fu aw>
                        <ProjectLinks project={currentProject} />
                      </Box>
                    </Box>
                    <P s="L" c="text-secondary">
                      {drop?.about?.highlightsText}
                    </P>
                  </TitleBox>
                </TitleCountdownBox>
              </Box>
            </TopWrapper>
          </Box>
          <ChapterNavigation
            drops={dropsWithChapters}
            currentDropChapterId={currentDropChapterId}
            onDropChapterChange={onDropChapterChange}
          />
          <Switch>
            <Route path={routes.drops.chapter}>
              <ChapterView
                drop={drop}
                chapterNumber={
                  dropsWithChapters.find((chapter) => chapter.id === drop.id)
                    ?.chapterNumber
                }
              />
            </Route>
            <Route path={routes.drops.slug}>
              <DropOverview drop={drop} chapters={dropsWithChapters} />
            </Route>
            <Route path="/*" render={() => <Redirect to="/" />} />
            <Route path="*" render={() => <Redirect to="/" />} />
          </Switch>
        </OuterWrapper>
      </Box>
    </>
  )
}

export type DropWithChapterNumber = IDrop & { chapterNumber: number }

function ChapterNavigation({
  drops,
  currentDropChapterId,
  onDropChapterChange,
}: {
  drops: DropWithChapterNumber[]
  currentDropChapterId: number
  onDropChapterChange: (id: number) => void
}) {
  const location = useLocation()
  const maxWidth = usePageMaxWidth()
  const { pathname } = location
  const history = useHistory()
  const slugPath = pathname.split('/').slice(0, 3).join('/')
  function navigateToChapter(id: number) {
    const isCurrentChapter = id === currentDropChapterId
    const pathHasSlash = pathname.endsWith('/')
    if (
      isCurrentChapter
      && pathname !== `${slugPath}${pathHasSlash ? '/' : ''}`
    ) {
      history.push(slugPath)
      return
    }
    // gohorse because of routing and trailing slash on env vs local lol
    history.push(`${pathname}${pathHasSlash ? '' : '/'}chapter/${id}`)
    onDropChapterChange(id)
  }
  const isChapterView = pathname.includes('chapter')
  const activeChapter = isChapterView ? pathname.split('/').pop() : undefined
  const containerRef = useRef<HTMLDivElement>(null)
  const showFixedTop = useRef(false)
  const { scrollY } = useScrollPosition()
  const isAtLeastExpandedPlus = useShouldShowForThreshold('expandedPlus')

  useEffect(() => {
    if (!containerRef.current) return
    if (showFixedTop.current === null || showFixedTop.current === undefined)
      return
    const containerPositionOnPage = containerRef.current.getBoundingClientRect()
    if (containerPositionOnPage.top < -16) {
      showFixedTop.current = true
    } else {
      showFixedTop.current = false
    }
  }, [scrollY, showFixedTop, containerRef, history])

  useEffect(() => {
    // on initial render, if the chapter is not active, scroll to the top of the page
    if (!activeChapter) {
      // smooth scroll to top of page
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }
    // on chapter change, scroll this container ref element into view at the top of the page
    if (containerRef.current) {
      const containerRefPositionOnPage = containerRef.current.offsetTop

      window.scrollTo({
        top: containerRefPositionOnPage + (isAtLeastExpandedPlus ? 138 : 94),
        behavior: 'smooth',
      })
    }
  }, [history, pathname, containerRef, activeChapter, isAtLeastExpandedPlus])

  return (
    <>
      <ChapterNavContainer g={!!activeChapter ? 0 : 12} ref={containerRef}>
        <Link to={slugPath}>
          <ChipNavButton
            $styleType="active-chip"
            isActive
            borderRadiusRightHide={!!activeChapter}
          >
            <Strong s="M">
              Drops
            </Strong>
          </ChipNavButton>
        </Link>
        {drops.map((dropChapter, index) => {
          const { id, chapterNumber } = dropChapter
          const isActive = activeChapter === id.toString()
          const shouldHide = !!activeChapter && !isActive
          const isDisabled = getIsDropChapterDisabled(dropChapter, index)
          return (
            <ChipNavButton
              key={`drop-chapter-${id}-block`}
              $styleType={
                isActive
                  ? 'active-chip'
                  : isDisabled
                    ? 'surface-chip-disabled'
                    : 'surface-chip'
              }
              onClick={() => navigateToChapter(id)}
              isActive={isActive}
              shouldHide={shouldHide}
              borderRadiusLeftHide={isActive}
              disabled={isDisabled}
            >
              <Strong s="M">
                Chapter {chapterNumber}
              </Strong>
            </ChipNavButton>
          )
        })}
      </ChapterNavContainer>
      {showFixedTop.current && (
        <ChapterNavContainer
          g={!!activeChapter ? 0 : 12}
          fixedTop
          maxWidth={maxWidth}
        >
          <Link to={slugPath}>
            <ChipNavButton
              $styleType="active-chip"
              isActive
              borderRadiusRightHide={!!activeChapter}
            >
              Drops
            </ChipNavButton>
          </Link>
          {drops.map((dropChapter, index) => {
            const { id, chapterNumber } = dropChapter
            const isActive = activeChapter === id.toString()
            const shouldHide = !!activeChapter && !isActive
            const isDisabled = getIsDropChapterDisabled(dropChapter, index)
            return (
              <ChipNavButton
                key={`drop-chapter-${id}-floating`}
                $styleType={
                  isActive
                    ? 'active-chip'
                    : isDisabled
                      ? 'surface-chip-disabled'
                      : 'surface-chip'
                }
                onClick={() => navigateToChapter(id)}
                isActive={isActive}
                shouldHide={shouldHide}
                borderRadiusLeftHide={isActive}
              >
                Chapter {chapterNumber}
              </ChipNavButton>
            )
          })}
        </ChapterNavContainer>
      )}
    </>
  )
}

type ChipNavButtonProps = {
  isActive?: boolean
  shouldHide?: boolean
  borderRadiusLeftHide?: boolean
  borderRadiusRightHide?: boolean
}
type ChapterNavContainerProps = {
  fixedTop?: boolean
  maxWidth?: number
}

const ChapterNavContainer = styled(Box) <ChapterNavContainerProps>`
  padding-top: 12px;
  padding-bottom: 24px;
  border-bottom: 1px solid var(--stroke-secondary, #333);
  ${({ maxWidth }) =>
    maxWidth &&
    css`
      max-width: ${maxWidth}px;
    `}
  @media screen and (max-width: ${({ theme }) => theme.breakpoints.compact}px) {
    ${overflowingCarouselCssRules}
  }
  ${({ fixedTop, maxWidth }) =>
    fixedTop &&
    css`
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      background: ${({ theme }) => theme.semantics.zap.surface.primary};
      z-index: 100;
      ${pageWrapCssRules};
      padding-top: 16px !important;
      padding-bottom: 16px !important;
      @media screen and (min-width: ${maxWidth}px) {
        left: auto;
        padding-left: 0 !important;
        padding-right: 0 !important;
        max-width: 1336px;
      }
    `}
`

export const ChipNavButton = styled(StyledThemedButton) <ChipNavButtonProps>`
  padding-right: 12px;
  padding-left: 12px;
  max-height:32px;
  ${({ shouldHide }) =>
    shouldHide &&
    css`
      opacity: 0;
      max-width: 0;
      padding: 0;
      margin: 0;
      overflow: hidden;
      pointer-events: none;
      border: 0px solid transparent;
    `}
  ${({ borderRadiusLeftHide }) =>
    borderRadiusLeftHide &&
    css`
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
    `}
  ${({ borderRadiusRightHide }) =>
    borderRadiusRightHide &&
    css`
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
    `}

    img.chain-icon {
    max-width: 20px;
    filter: unset !important;
  }

  ${defaultTransition}
`

const Banner = styled.img`
  border-radius: var(--units-8, 8px);
  height: 220px;
  width: 100%;
  object-fit: cover;
  object-position: center;
`
